import {getQueryParams} from "@COMPONENTS/common/commonUtil";
import {commonThemaCd} from "@COMPONENTS/common/constants";
import {emphasisCdLabel} from "@COMPONENTS/common/goodsUtil";
import {getTaggingData, menuForTagging} from "@COMPONENTS/common/tagging/taggingCommon";
import {FilterType} from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {GoodsByMenu} from "@QUERIES/prdt/goods/useGoodsByMenu";
import {GoodsCompare} from "@QUERIES/prdt/goods/useGoodsCompare";
import dayjs from "dayjs";

/** 필터 데이터 */
const filterForTagging = () => {
	try {
		const queryParams = getQueryParams();
		let filters: FilterType[] = null;
		const rawData = sessionStorage.getItem(`persist:$$YBSESSION_PRDT_FILTER_${publicRuntimeConfig.RUNTIME_ENV}`);
		if (rawData) {
			const parsedData = JSON.parse(rawData);
			filters = parsedData.goodsFilter ? JSON.parse(parsedData.goodsFilter)[queryParams.dspSid] : null;
		}

		// 정렬 및 필터 정보
		let sortOrderNm = "-";
		let filterDtlSearch = "-";
		let filterDeprtMn = "-";
		let filterDeprtDt = "-";
		let filterDeprtTime = "-";
		let filterDeprtCity = "-";
		let filterNight = "-";
		let filterPriceMin = "-";
		let filterPriceMax = "-";
		let filterFlight = "-";
		let filterFeature = "-";
		let filterPromo = "-";
		let filterMem = "-";
		if (filters) {
			// 정렬
			sortOrderNm = filters.find(e => e.key === "sort")?.text || "-";
			// 상세검색
			filterDtlSearch = filters.find(e => e.key === "goodsNm")?.text || "-";
			// 출발일정
			const departureDate = filters.find(e => e.key === "outStartDateList")?.value;
			if (departureDate && departureDate.length === 6) {
				filterDeprtMn = dayjs(departureDate).format("YYYY-MM") || "-";
			} else {
				filterDeprtDt = dayjs(departureDate).format("YYYY-MM-DD") || "-";
			}
			// 출발시간
			filterDeprtTime =
				filters
					.filter(e => e.key === "outDeprtFgList")
					?.map(t => t.text)
					?.join(",") || "-";
			// 출발지
			filterDeprtCity =
				filters
					.filter(e => e.key === "outCityDiviNm")
					?.map(t => t.text)
					?.join(",") || "-";
			// 여행기간
			filterNight =
				filters
					.filter(e => e.key === "evDayCntList")
					?.map(t => t.text)
					?.join(",") || "-";
			// 최소금액
			filterPriceMin = filters.find(e => e.key === "minPrice")?.value || "-";
			// 최대금액
			filterPriceMax = filters.find(e => e.key === "maxPrice")?.value || "-";
			// 이용교통
			filterFlight =
				filters
					.filter(e => e.key === "airCd")
					?.map(t => t.text)
					?.join(",") || "-";
			// 상품특징
			filterFeature =
				filters
					.filter(e => e.key === "goodsFeaturesList")
					?.map(t => t.text)
					?.join(",") || "-";
			// 프로모션
			filterPromo =
				filters
					.filter(e => e.key === "goodsPromotion")
					?.map(t => t.text)
					?.join(",") || "-";
			// 여행구성원
			filterMem =
				filters
					.filter(e => e.key === "goodsTmember")
					?.map(t => t.text)
					?.join(",") || "-";
		}

		return {
			sortOrderNm,
			filterDtlSearch,
			filterDeprtMn,
			filterDeprtDt,
			filterDeprtTime,
			filterDeprtCity,
			filterNight,
			filterPriceMin,
			filterPriceMax,
			filterFlight,
			filterFeature,
			filterPromo,
			filterMem,
		};
	} catch (error) {
		return null;
	}
};

/** ```
 * braze, apmplitude - pkg_view_goodsCdList
 * @description 상품전시 리스트 로드
 * ```*/
export const taggingLoadLocalList = () => {
	try {
		const menuData = menuForTagging();
		const goodsByMenu = getTaggingData("useGoodsByMenu");
		let continentCd = "-";
		let continentNm = "-";
		let countryCd = "-";
		let countryNm = "-";
		if (Array.isArray(goodsByMenu)) {
			const goods = goodsByMenu[0] as GoodsByMenu;
			continentCd = goods.continentCd;
			continentNm = goods.continentNm;
			countryCd = goods.countryCd;
			countryNm = goods.countryNm;
		}

		return {
			...menuData,
			continentCd,
			continentNm,
			countryCd,
			countryNm,
		};
	} catch (e) {
		return null;
	}
};

/** ```
 * Braze, Amplitude - 상품 비교 pkg_compare_goodsCd
 * @description 상품 비교 로드
 * ```*/
export const taggingLoadCompareGoods = (compareList: GoodsCompare[]) => {
	try {
		if (!compareList || !compareList.length) return;

		const menuData = menuForTagging();
		const filterData = filterForTagging();

		const product1 = compareList[0];
		const product2 = compareList[1];
		const product3 = compareList[2];

		// 첫번째 비교상품
		const goodsCdSeq1 = product1.goodsCd;
		const goodsNmSeq1 = product1.goodsNm;
		let countryCdSeq1 = "-";
		let countryNmSeq1 = "-";
		// 두번째 비교상품
		const goodsCdSeq2 = product2.goodsCd;
		const goodsNmSeq2 = product2.goodsNm;
		let countryCdSeq2 = "-";
		let countryNmSeq2 = "-";
		// 세번째 비교상품
		const goodsCdSeq3 = product3?.goodsCd || "-";
		const goodsNmSeq3 = product3?.goodsNm || "-";
		let countryCdSeq3 = "-";
		let countryNmSeq3 = "-";

		// USE_GOODS_BY_MENU
		let continentCd = "-";
		let continentNm = "-";
		const goodsByMenu = getTaggingData("useGoodsByMenu") as GoodsByMenu[];
		if (Array.isArray(goodsByMenu)) {
			const p1 = goodsByMenu.find(e => e.goodsCd === goodsCdSeq1);
			const p2 = goodsByMenu.find(e => e.goodsCd === goodsCdSeq2);
			const p3 = goodsByMenu.find(e => e.goodsCd === goodsCdSeq3);
			// 공통
			continentCd = p1.continentCd;
			continentNm = p1.continentNm;
			// 상품비교 1
			countryCdSeq1 = p1.countryCd;
			countryNmSeq1 = p1.countryNm;
			// 상품비교 2
			countryCdSeq2 = p2.countryCd;
			countryNmSeq2 = p2.countryNm;
			// 상품비교 3 (모바일 해당 X)
			if (p3) {
				countryCdSeq3 = p3.countryCd;
				countryNmSeq3 = p3.countryNm;
			}
		}

		return {
			...menuData,
			...filterData,
			continentCd,
			continentNm,
			goodsCdSeq1,
			goodsNmSeq1,
			countryCdSeq1,
			countryNmSeq1,
			goodsCdSeq2,
			goodsNmSeq2,
			countryCdSeq2,
			countryNmSeq2,
			goodsCdSeq3,
			goodsNmSeq3,
			countryCdSeq3,
			countryNmSeq3,
		};
	} catch (e) {
		return null;
	}
};

/**
 * braze, amplitude - pkg_select_prod_list
 * @description 상품 리스트 상품 클릭 이벤트
 * @param goodsCd string
 */
export const taggingClickGoods = (props: {goodsCd: string}) => {
	try {
		const {goodsCd} = props;
		const filterData = filterForTagging();
		const menuData = menuForTagging();
		const goodsByMenu = getTaggingData("useGoodsByMenu") as GoodsByMenu[];
		const goods = goodsByMenu.find(v => v.goodsCd === goodsCd);

		const {goodsNm, continentCd, continentNm, countryCd, countryNm, minPrice} = goods;
		const {goodsEmphasisDivi, themaDivi, bestYn, rvwAvgScore} = goods;
		const goodsPromotion = goods?.goodsPromotion ? goods?.goodsPromotion?.split(",") : undefined;
		const goodsTmember = goods?.goodsTmember ? goods?.goodsTmember?.split(",") : undefined;
		const featuresDivi = goods.goodsFeaturesDiviList?.split("|");
		const featuresDiviCds = [];
		const featuresDiviNms = [];
		featuresDivi?.forEach(f => {
			const nm = f.split(",")[0];
			const cd = f.split(",")[1];
			featuresDiviNms.push(nm);
			featuresDiviCds.push(cd);
		});

		const features = goods.goodsFeaturesList?.split("|");
		const featuresCds = [];
		const featuresNms = [];
		features?.forEach(f => {
			const nm = f.split(",")[0];
			const cd = f.split(",")[1];
			featuresNms.push(nm);
			featuresCds.push(cd);
		});

		return {
			data: {
				...menuData,
				continentCd,
				continentNm,
				goodsCd,
				goodsNm,
				goodsPrice: minPrice ?? "-",
				countryCd,
				countryNm,
				...filterData,
				emphasisCd: goodsEmphasisDivi ?? "-",
				emphasisNm: emphasisCdLabel(goodsEmphasisDivi) ?? "-",
				themaCd: themaDivi ?? "-",
				themaNm: commonThemaCd[themaDivi] ?? "-",
				bestYn: bestYn ?? "N",
				promoCd: Array.isArray(goodsPromotion) ? goodsPromotion[0] : "-",
				promoNm: Array.isArray(goodsPromotion) ? goodsPromotion[1] : "-",
				featuresDivCd: featuresDiviCds.join(",") || "-",
				featuresDivNm: featuresDiviNms.join(",") || "-",
				featuresCd: featuresCds.join(",") || "-",
				featuresNm: featuresNms.join(",") || "-",
				memCd: Array.isArray(goodsTmember) ? goodsTmember[0] : "-",
				memNm: Array.isArray(goodsTmember) ? goodsTmember[1] : "-",
				score: rvwAvgScore,
			},
			attribute: {
				...menuData,
				date: dayjs().format("YYYYMMDDHHmmss"),
				goodsCd,
				continentNm,
				countryNm,
				featuresNm: featuresNms.join(","),
				memNm: Array.isArray(goodsTmember) ? goodsTmember[1] : "-",
				score: rvwAvgScore,
			},
		};
	} catch (e) {
		return {data: null, attribute: null};
	}
};

/** ```
 * Braze - 상품공유 클릭 이벤트 pkg_share_goodsCd
 * ```*/
export const taggingClickShareGoods = (props: {parameter: string; shareCh: string}) => {
	try {
		const {parameter, shareCh} = props;
		const filterData = filterForTagging();
		const menuData = menuForTagging();
		const queryParams = getQueryParams(parameter);
		const goodsCd = queryParams.goodsCd || "-";
		let goodsNm = "-";

		// USE_GOODS_BY_MENU
		const goodsByMenu = getTaggingData("useGoodsByMenu") as GoodsByMenu[];
		let continentCd = "-";
		let continentNm = "-";
		let countryCd = "-";
		let countryNm = "-";
		if (Array.isArray(goodsByMenu)) {
			const goods = goodsByMenu[0];
			continentCd = goods.continentCd;
			continentNm = goods.continentNm;
			countryCd = goods.countryCd;
			countryNm = goods.countryNm;
		}

		if (goodsCd && Array.isArray(goodsByMenu)) {
			// 공유하기 타겟 상품 찾기
			const goods = goodsByMenu.find(e => e.goodsCd === goodsCd);
			goodsNm = goods?.goodsNm || "-";
		}
		return {
			...menuData,
			...filterData,
			continentCd,
			continentNm,
			countryCd,
			countryNm,
			goodsCd,
			goodsNm,
			shareCh,
		};
	} catch (e) {
		return null;
	}
};
